import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faVial, faServer, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../../config';

function MCPConfiguration() {
  const [mcpServers, setMcpServers] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [currentMcp, setCurrentMcp] = useState(null);
  
  // Form states
  const [frameworkId, setFrameworkId] = useState('');
  const [name, setName] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [configSchema, setConfigSchema] = useState('{\n  "url": "string",\n  "consumer_key": "string",\n  "consumer_secret": "string",\n  "store_id": "string"\n}');
  
  // Testing state
  const [testingStatus, setTestingStatus] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch MCP servers
      const mcpResponse = await axios.get(`${API_URL}/mcp`);
      setMcpServers(mcpResponse.data.mcpServers || []);
      
      // Fetch frameworks for dropdown
      const frameworkResponse = await axios.get(`${API_URL}/frameworks`);
      setFrameworks(frameworkResponse.data.frameworks || []);
      
      setError('');
    } catch (err) {
      console.error('Error fetching MCP data:', err);
      setError('Failed to load MCP configuration');
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setFrameworkId('');
    setName('');
    setServerUrl('https://woocommerce-mcp.techspawn.workers.dev');
    setApiKey('3dG8H2bJ9qL1mN5rT6uV0wX7zP4sQ8oY2kI1');
    setConfigSchema('{\n  "url": "string",\n  "consumer_key": "string",\n  "consumer_secret": "string",\n  "store_id": "string"\n}');
  };
  
  const handleAddServer = async () => {
    try {
      // Validate JSON format
      JSON.parse(configSchema);
      
      setError('');
      
      await axios.post(`${API_URL}/mcp`, {
        frameworkId,
        name,
        serverUrl,
        apiKey,
        configSchema
      });
      
      setSuccess('MCP server added successfully');
      setShowAddModal(false);
      resetForm();
      
      // Refresh MCP servers list
      fetchData();
    } catch (err) {
      console.error('Error adding MCP server:', err);
      if (err.message.includes('JSON')) {
        setError(`Invalid JSON format: ${err.message}`);
      } else {
        setError(err.response?.data?.message || 'Error adding MCP server');
      }
    }
  };
  
  const handleEditServer = async () => {
    try {
      // Validate JSON format
      JSON.parse(configSchema);
      
      setError('');
      
      await axios.put(`${API_URL}/mcp`, {
        mcpId: currentMcp.id,
        name,
        serverUrl,
        apiKey,
        configSchema
      });
      
      setSuccess('MCP server updated successfully');
      setShowEditModal(false);
      
      // Refresh MCP servers list
      fetchData();
    } catch (err) {
      console.error('Error updating MCP server:', err);
      if (err.message.includes('JSON')) {
        setError(`Invalid JSON format: ${err.message}`);
      } else {
        setError(err.response?.data?.message || 'Error updating MCP server');
      }
    }
  };
  
  const handleDeleteServer = async () => {
    try {
      setError('');
      
      await axios.delete(`${API_URL}/mcp/${currentMcp.id}`);
      
      setSuccess('MCP server deleted successfully');
      setShowDeleteModal(false);
      
      // Refresh MCP servers list
      fetchData();
    } catch (err) {
      console.error('Error deleting MCP server:', err);
      setError(err.response?.data?.message || 'Error deleting MCP server');
    }
  };
  
  const handleTestServer = async () => {
    try {
      setTestingStatus({ status: 'testing' });
      
      const response = await axios.post(`${API_URL}/mcp/test`, {
        serverUrl: currentMcp.server_url,
        apiKey: currentMcp.api_key
      });
      
      if (response.data.success) {
        setTestingStatus({ 
          status: 'success', 
          message: 'Connection successful!'
        });
      } else {
        setTestingStatus({ 
          status: 'error', 
          message: response.data.message || 'Connection failed.'
        });
      }
    } catch (err) {
      console.error('Error testing MCP server:', err);
      setTestingStatus({ 
        status: 'error', 
        message: err.response?.data?.message || 'Connection failed.'
      });
    }
  };
  
  const openEditModal = (mcp) => {
    setCurrentMcp(mcp);
    setName(mcp.name);
    setServerUrl(mcp.server_url);
    setApiKey(mcp.api_key);
    
    // Format the JSON schema for readability
    try {
      const schema = typeof mcp.config_schema === 'string' ? 
        JSON.parse(mcp.config_schema) : mcp.config_schema;
      setConfigSchema(JSON.stringify(schema, null, 2));
    } catch (e) {
      setConfigSchema(mcp.config_schema);
    }
    
    setShowEditModal(true);
  };
  
  const openDeleteModal = (mcp) => {
    setCurrentMcp(mcp);
    setShowDeleteModal(true);
  };
  
  const openTestModal = (mcp) => {
    setCurrentMcp(mcp);
    setTestingStatus(null);
    setShowTestModal(true);
  };
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading MCP configuration...</p>
      </div>
    );
  }
  
  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="m-0">
            <FontAwesomeIcon icon={faServer} className="me-2" />
            MCP Servers
          </h5>
          <div>
            <Button 
              variant="outline-info" 
              size="sm" 
              className="me-2"
              onClick={() => setShowHelpModal(true)}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" /> Help
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add MCP Server
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {mcpServers.length === 0 ? (
            <p className="text-center">No MCP servers configured yet.</p>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Framework</th>
                  <th>Name</th>
                  <th>Server URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mcpServers.map(server => (
                  <tr key={server.id}>
                    <td>{server.framework_name}</td>
                    <td>{server.name}</td>
                    <td>{server.server_url}</td>
                    <td>
                      <Button variant="info" size="sm" className="me-1" onClick={() => openEditModal(server)}>
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Button>
                      <Button variant="danger" size="sm" className="me-1" onClick={() => openDeleteModal(server)}>
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </Button>
                      <Button variant="success" size="sm" onClick={() => openTestModal(server)}>
                        <FontAwesomeIcon icon={faVial} /> Test
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      {/* Add MCP Server Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Add MCP Server
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Framework</Form.Label>
              <Form.Select 
                value={frameworkId}
                onChange={(e) => setFrameworkId(e.target.value)}
                required
              >
                <option value="">Select Framework</option>
                {frameworks.map(framework => (
                  <option key={framework.id} value={framework.id}>{framework.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">MCP Name</Form.Label>
              <Form.Control 
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="E.g., WooCommerce MCP"
                required
              />
              <Form.Text className="text-muted">
                A descriptive name for this MCP server configuration
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Server URL</Form.Label>
              <Form.Control 
                type="url"
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="E.g., https://woocommerce-mcp.techspawn.workers.dev"
                required
              />
              <Form.Text className="text-muted">
                The MCP server endpoint URL
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">API Key</Form.Label>
              <Form.Control 
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="API key for MCP server"
                required
              />
              <Form.Text className="text-muted">
                The API key used for authentication with the MCP server
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Configuration Schema (JSON)</Form.Label>
              <Form.Control 
                as="textarea"
                rows={6}
                value={configSchema}
                onChange={(e) => setConfigSchema(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Define the required configuration fields for this MCP in JSON format
              </Form.Text>
            </Form.Group>
            
            <Alert variant="info">
              <h6 className="alert-heading">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                MCP Configuration Help
              </h6>
              <p>For WooCommerce MCP, use the configuration schema from the Usage Guide.</p>
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddServer}>Add Server</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Edit MCP Server Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit MCP Server
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentMcp && (
            <Form>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Framework</Form.Label>
                <Form.Control
                  type="text"
                  value={currentMcp.framework_name}
                  readOnly
                />
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">MCP Name</Form.Label>
                <Form.Control 
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Server URL</Form.Label>
                <Form.Control 
                  type="url"
                  value={serverUrl}
                  onChange={(e) => setServerUrl(e.target.value)}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">API Key</Form.Label>
                <Form.Control 
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Configuration Schema (JSON)</Form.Label>
                <Form.Control 
                  as="textarea"
                  rows={6}
                  value={configSchema}
                  onChange={(e) => setConfigSchema(e.target.value)}
                  required
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditServer}>Update Server</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete MCP Server Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className="text-danger me-2" />
            Delete MCP Server
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentMcp && (
            <div className="alert alert-danger">
              <p>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                Are you sure you want to delete the MCP server "{currentMcp.name}" for the {currentMcp.framework_name} framework?
              </p>
              <p>Warning: This action cannot be undone.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteServer}>Delete Server</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Test MCP Server Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faVial} className="text-success me-2" />
            Test MCP Server
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentMcp && (
            <div>
              <div className="alert alert-info">
                <p>This will test the connection to the MCP server "{currentMcp.name}" by calling its health endpoint.</p>
                <p><strong>Server URL:</strong> {currentMcp.server_url}</p>
              </div>
              
              {testingStatus === null && (
                <div className="text-center">
                  <Button variant="success" onClick={handleTestServer}>
                    Test Connection
                  </Button>
                </div>
              )}
              
              {testingStatus && testingStatus.status === 'testing' && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Testing connection...</span>
                  </div>
                  <p className="mt-2">Testing connection...</p>
                </div>
              )}
              
              {testingStatus && testingStatus.status === 'success' && (
                <div className="alert alert-success">
                  <h6 className="alert-heading">Connection Successful!</h6>
                  <p>{testingStatus.message}</p>
                </div>
              )}
              
              {testingStatus && testingStatus.status === 'error' && (
                <div className="alert alert-danger">
                  <h6 className="alert-heading">Connection Failed</h6>
                  <p>{testingStatus.message}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      
      {/* MCP Help Modal */}
      <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} size="lg" className="modal-dialog-scrollable">
        <Modal.Header closeButton>
          <Modal.Title>WooCommerce MCP Server Usage Guide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h5>Server Information</h5>
            <p><strong>Server URL:</strong> https://woocommerce-mcp.techspawn.workers.dev</p>
            <p><strong>API Key:</strong> 3dG8H2bJ9qL1mN5rT6uV0wX7zP4sQ8oY2kI1</p>
          </div>
          
          <div className="mb-4">
            <h5>Available Tools</h5>
            <ul className="list-group">
              <li className="list-group-item d-flex justify-content-between align-items-center">
                get_products
                <span className="badge bg-primary rounded-pill">Products</span>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-center">
                create_product
                <span className="badge bg-primary rounded-pill">Products</span>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-center">
                get_orders
                <span className="badge bg-success rounded-pill">Orders</span>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-center">
                update_order
                <span className="badge bg-success rounded-pill">Orders</span>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-center">
                get_customers
                <span className="badge bg-info rounded-pill">Customers</span>
              </li>
              <li className="list-group-item d-flex justify-content-between align-items-center">
                get_sales_report
                <span className="badge bg-warning text-dark rounded-pill">Reports</span>
              </li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h5>How to Get WooCommerce API Keys</h5>
            <ol>
              <li>Log in to WordPress admin</li>
              <li>Go to WooCommerce → Settings → Advanced → REST API</li>
              <li>Click "Add Key"</li>
              <li>Enter a description (e.g., "AI Integration")</li>
              <li>Set Permissions to "Read/Write"</li>
              <li>Click "Generate API Key"</li>
              <li>Copy the Consumer Key and Consumer Secret</li>
            </ol>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowHelpModal(false)}>Got it!</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MCPConfiguration;
